import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, MapPin, Link as LinkIcon, Download, Filter, Check, X, Clock, MessageSquare, AlertTriangle, Trash2, ExternalLink, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NAVY = "#1E2761";
const GOLD = "#C9A227";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("gyp_admin_token") || "");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("barcelona");
  const [ambassadors, setAmbassadors] = useState([]);
  const [barcelona, setBarcelona] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filters, setFilters] = useState({ seat_type: "", country: "", review_status: "", payment_status: "paid" });
  const [expanded, setExpanded] = useState(null);

  const loadAll = async (t) => {
    setLoading(true);
    try {
      const [ambRes, barRes] = await Promise.all([
        axios.get(`${API}/admin/ambassadors`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/barcelona`, { headers: { "X-Admin-Token": t } }),
      ]);
      setAmbassadors(ambRes.data);
      setBarcelona(barRes.data);
      setLoaded(true);
      localStorage.setItem("gyp_admin_token", t);
      setToken(t);
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Wrong password" : "Failed to load");
      localStorage.removeItem("gyp_admin_token");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gyp_admin_token");
    setToken(""); setAmbassadors([]); setBarcelona([]); setLoaded(false); setInput("");
  };

  const updateReview = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/barcelona/${id}`, { review_status: status }, { headers: { "X-Admin-Token": token } });
      setBarcelona(b => b.map(r => r.id === id ? { ...r, review_status: status } : r));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteRegistration = async (id) => {
    if (!window.confirm("Delete this unpaid registration? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/barcelona/${id}`, { headers: { "X-Admin-Token": token } });
      setBarcelona(b => b.filter(r => r.id !== id));
      setExpanded(null);
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/barcelona/export`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "barcelona-2027-registrations.csv";
      a.click(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (token && !loaded && !loading) loadAll(token);

  const filteredBarcelona = useMemo(() => {
    return barcelona.filter(r =>
      (!filters.seat_type || r.seat_type === filters.seat_type) &&
      (!filters.country || (r.country || "").toLowerCase().includes(filters.country.toLowerCase())) &&
      (!filters.review_status || r.review_status === filters.review_status) &&
      (!filters.payment_status || r.payment_status === filters.payment_status)
    );
  }, [barcelona, filters]);

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-paper">
        <div className="w-full max-w-sm bg-white border border-black/10 p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="h-5 w-5" style={{ color: NAVY }} strokeWidth={1.5} />
            <h1 className="font-serif text-2xl" style={{ color: NAVY }}>Admin Access</h1>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); loadAll(input); }}>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Password</label>
            <input
              type="password" value={input} onChange={(e) => setInput(e.target.value)}
              className="w-full border border-black/15 p-3 bg-transparent focus:outline-none mb-4"
              style={{ borderColor: `${NAVY}22` }}
              data-testid="admin-password-input" autoFocus
            />
            <button type="submit" disabled={loading || !input}
              className="w-full py-3 text-xs tracking-[0.2em] uppercase text-white disabled:opacity-50"
              style={{ backgroundColor: NAVY }} data-testid="admin-login-btn">
              {loading ? "Loading..." : "Enter"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-paper py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-end mb-6 pb-4 border-b border-black/10">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted mb-2">Admin Dashboard</p>
            <h1 className="font-serif text-3xl" style={{ color: NAVY }}>GYP Applications</h1>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted hover:text-black" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-black/10 mb-6">
          <TabBtn active={tab === "barcelona"} onClick={() => setTab("barcelona")} count={barcelona.filter(r => r.payment_status === "paid").length} label="Barcelona 2027" testid="tab-barcelona" />
          <TabBtn active={tab === "ambassadors"} onClick={() => setTab("ambassadors")} count={ambassadors.length} label="Ambassadors" testid="tab-ambassadors" />
        </div>

        {loading && <p className="text-muted text-center py-12">Loading...</p>}

        {/* Barcelona tab */}
        {!loading && tab === "barcelona" && (
          <>
            <div className="flex flex-wrap gap-3 items-center mb-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-gray-500">
                <Filter className="h-3.5 w-3.5" /> Filters:
              </div>
              <FilterSelect value={filters.seat_type} onChange={v => setFilters(f => ({...f, seat_type: v}))} placeholder="Seat type" options={[["self_funded","Self-Funded"],["fully_funded","Fully-Funded"]]} />
              <FilterSelect value={filters.review_status} onChange={v => setFilters(f => ({...f, review_status: v}))} placeholder="Review" options={[["pending","Pending"],["approved","Approved"],["rejected","Rejected"],["auto_approved","Auto Approved"]]} />
              <input value={filters.country} onChange={e => setFilters(f => ({...f, country: e.target.value}))} placeholder="Country contains..." className="border p-2 text-sm" style={{ borderColor: `${NAVY}22` }} />
              <button onClick={downloadCsv} className="ml-auto flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase text-white" style={{ backgroundColor: GOLD }} data-testid="export-csv-btn">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
            <p className="text-sm text-muted mb-4">Showing {filteredBarcelona.length} paid registration{filteredBarcelona.length === 1 ? "" : "s"}</p>

            {filteredBarcelona.length === 0 && <p className="text-muted text-center py-16">No registrations match.</p>}

            <div className="space-y-3">
              {filteredBarcelona.map(r => {
                const photoUrl = r.photo_path ? `${API}/files/${r.photo_path}` : "";
                const isPaid = r.payment_status === "paid";
                const isUnpaid = r.payment_status !== "paid";
                return (
                <div key={r.id} className="bg-white border" style={{ borderColor: isUnpaid ? "#F59E0B55" : `${NAVY}15` }} data-testid={`bar-row-${r.id}`}>
                  {isUnpaid && (
                    <div className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-medium" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }} data-testid={`unpaid-banner-${r.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Unpaid — This applicant started registration but did not complete payment
                    </div>
                  )}
                  <div className="p-4 flex flex-wrap items-center gap-4">
                    {photoUrl ? (
                      <a href={photoUrl} target="_blank" rel="noreferrer" className="shrink-0">
                        <img src={photoUrl} alt={r.full_name} className="h-14 w-14 object-cover border" style={{ borderColor: `${NAVY}22` }} />
                      </a>
                    ) : (
                      <div className="h-14 w-14 shrink-0 bg-black/5 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-400" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-serif text-lg" style={{ color: NAVY }}>{r.full_name}</p>
                      <p className="text-xs text-gray-500">{r.email} · {r.whatsapp}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Country</p>
                      <p style={{ color: NAVY }}>{r.country}</p>
                    </div>
                    <div className="text-sm">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Seat</p>
                      <p style={{ color: isPaid ? NAVY : "#999", textDecoration: isPaid ? "none" : "line-through" }}>
                        {r.seat_type === "self_funded" ? "Self-Funded" : "Fully-Funded"} · ${r.amount}
                      </p>
                    </div>
                    <StatusPill label={r.payment_status} tone={isPaid ? "green" : "amber"} />
                    <StatusPill label={r.review_status?.replace("_"," ")} tone={
                      r.review_status === "approved" || r.review_status === "auto_approved" ? "green" :
                      r.review_status === "rejected" ? "red" : "amber"
                    } />
                    <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="text-xs uppercase tracking-[0.15em] px-3 py-1.5 border" style={{ color: NAVY, borderColor: `${NAVY}33` }} data-testid={`view-btn-${r.id}`}>
                      {expanded === r.id ? "Hide" : "View"}
                    </button>
                  </div>

                  {expanded === r.id && (
                    <div className="border-t p-6 bg-gray-50 space-y-5 text-sm" style={{ borderColor: `${NAVY}15` }} data-testid={`details-${r.id}`}>
                      {/* Delegate profile */}
                      <div className="flex gap-5 items-start">
                        {photoUrl ? (
                          <a href={photoUrl} target="_blank" rel="noreferrer" className="shrink-0">
                            <img src={photoUrl} alt={r.full_name} className="h-28 w-28 object-cover border" style={{ borderColor: `${NAVY}22` }} />
                          </a>
                        ) : (
                          <div className="h-28 w-28 shrink-0 bg-black/5 flex items-center justify-center">
                            <User className="h-10 w-10 text-gray-400" strokeWidth={1.25} />
                          </div>
                        )}
                        <div className="flex-1 grid grid-cols-2 gap-4">
                          <KV k="Full Name" v={r.full_name} />
                          <KV k="Age" v={r.age} />
                          <KV k="Email" v={<a href={`mailto:${r.email}`} className="hover:underline" style={{ color: NAVY }}>{r.email}</a>} />
                          <KV k="WhatsApp" v={<a href={`https://wa.me/${(r.whatsapp||"").replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: NAVY }}>{r.whatsapp}</a>} />
                          <KV k="Country" v={r.country} />
                          <KV k="City / Organization" v={r.organization || "—"} />
                          <KV k="Ambassador Code" v={r.ambassador_code || "—"} />
                          <KV k="Media Consent" v={r.media_consent ? "Yes" : "No"} />
                        </div>
                      </div>

                      {/* Payment info */}
                      <div className="p-4 border" style={{ borderColor: `${NAVY}15`, backgroundColor: "white" }}>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Payment</p>
                        <div className="grid grid-cols-4 gap-3 text-sm">
                          <KV k="Seat Type" v={r.seat_type === "self_funded" ? "Self-Funded" : "Fully-Funded"} />
                          <KV k="Amount" v={`$${r.amount}`} />
                          <KV k="Status" v={<span className="font-medium" style={{ color: isPaid ? "#065F46" : "#92400E" }}>{r.payment_status?.toUpperCase()}</span>} />
                          <KV k="Submitted" v={new Date(r.created_at).toLocaleString()} />
                        </div>
                        {r.session_id && (
                          <p className="text-[10px] text-gray-400 mt-3 font-mono break-all">
                            Stripe Session: {r.session_id}
                          </p>
                        )}
                      </div>

                      {r.motivation && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-1">Motivation</p>
                          <p className="whitespace-pre-wrap text-gray-800">{r.motivation}</p>
                        </div>
                      )}
                      {r.screening && Object.values(r.screening).some(Boolean) && (
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2 flex items-center gap-2">
                            <MessageSquare className="h-3 w-3" /> Screening Answers
                          </p>
                          <div className="space-y-2 pl-4 border-l-2" style={{ borderColor: GOLD }}>
                            <ScreenQA q="Diplomacy means to you" a={r.screening.diplomacy_meaning} />
                            <ScreenQA q="Why this program" a={r.screening.why_this_program} />
                            <ScreenQA q="Prior experience" a={r.screening.prior_experience === "yes" ? `Yes — ${r.screening.prior_experience_detail}` : "No"} />
                            <ScreenQA q="Country to represent" a={r.screening.country_choice} />
                            <ScreenQA q="Global issue" a={r.screening.global_issue} />
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: `${NAVY}15` }}>
                        {r.requires_review && isPaid && (
                          <>
                            <button onClick={() => updateReview(r.id, "approved")} className="flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white bg-green-700 hover:bg-green-800" data-testid={`approve-${r.id}`}>
                              <Check className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button onClick={() => updateReview(r.id, "rejected")} className="flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white bg-red-700 hover:bg-red-800" data-testid={`reject-${r.id}`}>
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                            <button onClick={() => updateReview(r.id, "pending")} className="flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-[0.15em] border" style={{ color: NAVY, borderColor: NAVY }}>
                              <Clock className="h-3.5 w-3.5" /> Reset
                            </button>
                          </>
                        )}
                        {isUnpaid && (
                          <button onClick={() => deleteRegistration(r.id)} className="ml-auto flex items-center gap-1 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white bg-red-700 hover:bg-red-800" data-testid={`delete-${r.id}`}>
                            <Trash2 className="h-3.5 w-3.5" /> Delete Unpaid
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );})}
            </div>
          </>
        )}

        {/* Ambassadors tab */}
        {!loading && tab === "ambassadors" && (
          <>
            {ambassadors.length === 0 && <p className="text-muted text-center py-16">No ambassador applications yet.</p>}
            <div className="grid md:grid-cols-2 gap-4">
              {ambassadors.map((amb) => {
                const photoUrl = amb.photo_path ? `${API}/files/${amb.photo_path}` : (amb.photo || "");
                return (
                <div key={amb.id} className="bg-white border border-black/10 p-6 flex gap-5" data-testid={`amb-card-${amb.id}`}>
                  {photoUrl ? (
                    <a href={photoUrl} target="_blank" rel="noreferrer" className="shrink-0">
                      <img src={photoUrl} alt={amb.full_name} className="h-28 w-28 object-cover border border-black/10" />
                    </a>
                  ) : (
                    <div className="h-28 w-28 bg-black/5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-ink mb-1 truncate">{amb.full_name}</h3>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-muted mb-3">{amb.gender} · {amb.nationality}</p>
                    <div className="space-y-1.5 text-sm">
                      <a href={`mailto:${amb.email}`} className="flex items-center gap-2 text-ink hover:text-crimson">
                        <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{amb.email}</span>
                      </a>
                      <a href={`tel:${amb.phone}`} className="flex items-center gap-2 text-ink hover:text-crimson">
                        <Phone className="h-3.5 w-3.5 shrink-0" /> {amb.phone}
                      </a>
                      <div className="flex items-center gap-2 text-muted">
                        <MapPin className="h-3.5 w-3.5 shrink-0" /> {amb.city}, {amb.nationality}
                      </div>
                      <a href={amb.social_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-crimson hover:underline">
                        <LinkIcon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{amb.social_link}</span>
                      </a>
                    </div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-muted mt-3 pt-3 border-t border-black/10">
                      {new Date(amb.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );})}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, count, label, testid }) => (
  <button onClick={onClick} className="pb-3 relative text-sm uppercase tracking-[0.2em]" data-testid={testid}
    style={{ color: active ? NAVY : "#888", fontWeight: active ? 600 : 400 }}>
    {label} <span className="ml-1 text-xs" style={{ color: GOLD }}>({count})</span>
    {active && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: NAVY }} />}
  </button>
);
const FilterSelect = ({ value, onChange, placeholder, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)} className="border p-2 text-sm bg-white" style={{ borderColor: `${NAVY}22` }}>
    <option value="">{placeholder}</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const StatusPill = ({ label, tone }) => {
  const colors = { green: ["#065f46", "#d1fae5"], red: ["#991b1b", "#fee2e2"], amber: ["#92400e", "#fef3c7"], gray: ["#374151", "#f3f4f6"] };
  const [fg, bg] = colors[tone] || colors.gray;
  return <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1" style={{ color: fg, backgroundColor: bg }}>{label}</span>;
};
const Grid2 = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;
const KV = ({ k, v }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500">{k}</p>
    <p style={{ color: NAVY }}>{v}</p>
  </div>
);
const ScreenQA = ({ q, a }) => a ? (
  <div>
    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5">{q}</p>
    <p className="text-gray-800 whitespace-pre-wrap">{a}</p>
  </div>
) : null;
